/**
 * CESM OSINT - Onglet Rapport Simplifie
 * Console assistee + Historique des rapports
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wand2, Presentation, Download, ExternalLink, Copy,
  History, Clock, FileText, RefreshCw, AlertTriangle, Info
} from 'lucide-react';
import ReportConsole from '@/components/ReportConsole';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportTab = ({ token, user, addGlobalLog, currentScanId, currentReport, generateReport, isGeneratingReport }) => {
  const [magicReports, setMagicReports] = useState([]);
  const [gammaReports, setGammaReports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (token) fetchHistory();
  }, [token]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const [magicRes, gammaRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/magic/history`, authHeaders),
        axios.get(`${API_URL}/api/reports/gamma/history`, authHeaders),
      ]);
      setMagicReports(magicRes.data.reports || []);
      setGammaReports(gammaRes.data.reports || []);
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const downloadMagicPdf = async (report) => {
    if (!report?.url) {
      toast.error('Lien de telechargement non disponible');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}${report.url}`, {
        ...authHeaders,
        responseType: 'blob',
        validateStatus: (status) => status < 500,
      });
      if (response.status === 404) {
        toast.error('Ce rapport n\'est plus disponible sur le serveur. Regenerez-le depuis la console.');
        return;
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = report.filename || `magic_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge');
    } catch {
      toast.error('Erreur telechargement — regenerez le rapport depuis la console');
    }
  };

  const hasHistory = magicReports.length > 0 || gammaReports.length > 0;

  return (
    <div className="space-y-6" data-testid="report-tab">
      {/* Console de rapport */}
      <ReportConsole token={token} />

      {/* Historique des rapports */}
      <div className="border border-white/10 rounded-lg bg-black/30">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-white/50" />
            <span className="font-mono text-white/70 text-sm font-bold">HISTORIQUE DES RAPPORTS</span>
          </div>
          <button
            onClick={fetchHistory}
            disabled={loadingHistory}
            className="p-1.5 text-white/40 hover:text-white rounded transition-all"
            data-testid="refresh-history"
          >
            <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {!hasHistory ? (
          <div className="p-8 text-center">
            <FileText className="w-10 h-10 text-white/20 mx-auto mb-2" />
            <p className="font-mono text-white/40 text-sm">Aucun rapport genere</p>
            <p className="font-mono text-white/30 text-xs mt-1">Utilisez la console ci-dessus pour creer votre premier rapport</p>
          </div>
        ) : (
          <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
            {/* Magic Reports */}
            {magicReports.map((r, i) => (
              <div key={r.id || i} className="flex items-center justify-between bg-black/40 border border-purple-500/20 rounded p-3 hover:border-purple-500/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/10 rounded">
                    <Wand2 className="w-4 h-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-mono text-purple-300 text-sm font-bold">Magic Report</p>
                    <p className="font-mono text-white/40 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {r.statistics && (
                        <span className="ml-2 text-white/30">
                          {r.statistics.news_analyzed || 0} art. | EWS {r.statistics.ews_score || 0}/100
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => downloadMagicPdf(r)}
                  className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 font-mono text-xs rounded flex items-center gap-1.5 transition-all"
                  data-testid={`download-magic-${i}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
            ))}

            {/* Gamma Reports */}
            {gammaReports.map((r, i) => (
              <div key={r.generation_id || i} className="flex items-center justify-between bg-black/40 border border-orange-500/20 rounded p-3 hover:border-orange-500/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500/10 rounded">
                    <Presentation className="w-4 h-4 text-orange-400" />
                  </div>
                  <div>
                    <p className="font-mono text-orange-300 text-sm font-bold">
                      Gamma {r.report_type === 'executive' ? 'Executif' : r.report_type === 'strategic' ? 'Strategique' : 'Technique'}
                    </p>
                    <p className="font-mono text-white/40 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { navigator.clipboard.writeText(r.gamma_url); toast.success('Lien copie'); }}
                    className="p-1.5 text-white/30 hover:text-white transition-all"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a
                    href={r.gamma_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-orange-500/15 border border-orange-500/30 text-orange-300 hover:bg-orange-500/25 font-mono text-xs rounded flex items-center gap-1.5 transition-all"
                    data-testid={`open-gamma-${i}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Ouvrir
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Note */}
      <div className="p-3 bg-marine-cyan/5 border border-marine-cyan/20 rounded flex items-start gap-2 text-xs">
        <Info className="w-4 h-4 text-marine-cyan mt-0.5 shrink-0" />
        <p className="font-mono text-white/50">
          <strong className="text-marine-cyan">Sources :</strong> Tous les rapports utilisent les donnees OSINT en temps reel (RSS, GDELT, EIA, scenarios Monte Carlo).
          Le Magic Report produit un PDF detaille, Gamma produit des slides visuelles.
          Les rapports PDF sont stockes temporairement — regenerez-les si necessaire.
        </p>
      </div>
    </div>
  );
};

export default ReportTab;
